import { useEffect, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useDialogs } from '@toolpad/core/useDialogs';
import { SerializedEditorState } from 'lexical';
import { FormProvider, useForm } from 'react-hook-form';

import { GetTemplateSetParams, useGetTemplateSet } from 'api/hooks/private/templates/GetTemplateSet';

import { RichTextEditor } from 'components/Editor';
import { EmailContainer } from 'components/Editor/ContentContainers/EmailContainer';
import TextInput from 'components/Forms/TextInput';
import {
  EmailTemplateInputValues,
  EmailTemplateOutputValues,
  EmailTemplateSchema,
} from 'components/TemplateForms/TemplateSchema';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * Props for the EmailTemplateForm component.
 *
 * @interface EmailTemplateFormProps
 */
interface EmailTemplateFormProps {
  /**
   * The key identifying the template set to fetch.
   */
  templateKey: GetTemplateSetParams['templateKey'];

  /**
   * The unique ID of the template to be edited.
   * If undefined or empty, the form will not fetch any template data.
   */
  id: GetTemplateSetParams['id'];

  /**
   * Callback function called when the form is submitted successfully.
   * Receives the validated email template output values.
   * Should return a Promise that resolves when submission is complete.
   *
   * @param {EmailTemplateOutputValues} data - The validated form data.
   * @returns {Promise<void>}
   */
  onSubmit: (data: EmailTemplateOutputValues) => Promise<void>;
}

export function EmailTemplateForm({ templateKey, id, onSubmit }: EmailTemplateFormProps) {
  const dialogs = useDialogs();
  const [editor] = useLexicalComposerContext();
  const hasFormInitialized = useRef(false);

  // fetch template set
  const templateSet = useGetTemplateSet({
    variables: {
      templateKey: templateKey,
      id: id ?? '',
    },
    enabled: !!id,
  });

  // get template merge tags
  const mergeTags = templateSet.data?.mergeTags ?? {
    tree: [],
    map: {},
  };

  const form = useForm<EmailTemplateInputValues, unknown, EmailTemplateOutputValues>({
    mode: 'all',
    resolver: zodResolver(EmailTemplateSchema),
  });

  // initialize the form with the merge tag options and existing template
  useEffect(() => {
    // get existing email template to initialize the form with
    const existingEmailTemplate = templateSet.data?.templateSet?.email;

    if (!hasFormInitialized.current && existingEmailTemplate?.body) {
      // initialize form fields with existing values
      form.reset({
        ...existingEmailTemplate,
        merge_tags: mergeTags.map,
      });

      // parse the serialized state
      const editorState = editor.parseEditorState(existingEmailTemplate.body as SerializedEditorState);

      // update the editor state with existing template
      editor.update(() => {
        editor.setEditorState(editorState);
      });

      // mark form as initialized
      hasFormInitialized.current = true;
    }

    // Cleanup on unmount: Reset the ref to allow reinitialization on remount
    return () => {
      hasFormInitialized.current = false;
    };
  }, [templateSet.data, form.reset, form, mergeTags.map, editor]);

  useEffect(() => {
    const formErrors = form.formState.errors;
    console.log('Form Errors: ', formErrors);
    console.log('Form Value: ', form.getValues());

    if (formErrors?.body?.message && typeof formErrors?.body?.message === 'string') {
      dialogs.alert(formErrors.body.message);
    }
  }, [dialogs, form, form.formState.errors]);

  useEffect(() => {
    console.log('Form Dirty: ', form.formState.isDirty);
    console.log('Form Dirty Fields: ', form.formState.dirtyFields);
  }, [form.formState.dirtyFields, form.formState.isDirty]);

  return (
    <FormProvider {...form}>
      <form id="email" noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <TextInput name="options.bcc.0" label="BCC" disabled={form.formState.isSubmitting} />
          <TextInput
            name="options.reply_to"
            label="Reply To Address"
            helperText="This is where replies are sent"
            disabled={form.formState.isSubmitting}
          />
          <TextInput name="subject" label="Email Subject" disabled={form.formState.isSubmitting} />
          <Box>
            <RichTextEditor
              contentEditableWrapper={EmailContainer}
              isLoading={templateSet.isLoading}
              mergeTags={mergeTags}
              onChange={(state) => form.setValue('body', state, { shouldDirty: true, shouldValidate: true })}
            />
          </Box>
          <Button variant="contained" type="submit" loading={form.formState.isSubmitting}>
            Save
          </Button>
        </Stack>
        <Backdrop
          open={form.formState.isSubmitting}
          sx={{
            zIndex: (theme) => (theme.zIndex.drawer + 1),
          }}
        >
          <CircularProgress />
        </Backdrop>
      </form>
    </FormProvider>
  );
}

export default EmailTemplateForm;
